/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { createRef, useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSwipeable } from 'react-swipeable';
import type { BuildACarouselProps } from './types';

export const useBuildACarousel = (props: BuildACarouselProps) => {
  const { arrayLength, lineItemRefs, scrollableContainerRef } = props;
  const scrollableCarouselPosition = scrollableContainerRef.current?.getBoundingClientRect();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hidePreviousButton, setHidePreviousButton] = useState(true);
  const [hideNextButton, setHideNextButton] = useState(true);
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));

  // Values derived from refs will otherwise be out of sync
  const getIsCarouselFullyScrolledToRight = useCallback(
    () =>
      lineItemRefs.current[lineItemRefs.current.length - 1]?.current?.getBoundingClientRect()
        .right < (scrollableCarouselPosition?.right ?? Infinity),
    [lineItemRefs, scrollableCarouselPosition?.right],
  );

  const getIsCarouselFullyScrolledToLeft = useCallback(
    () =>
      lineItemRefs.current[0]?.current?.getBoundingClientRect().left >=
      (scrollableCarouselPosition?.left ?? 0),
    [lineItemRefs, scrollableCarouselPosition?.left],
  );

  const handleButtonToggleOnScroll = useCallback(() => {
    setHideNextButton(getIsCarouselFullyScrolledToRight());
    setHidePreviousButton(getIsCarouselFullyScrolledToLeft());
  }, [getIsCarouselFullyScrolledToLeft, getIsCarouselFullyScrolledToRight]);

  const handleScrollTo = useCallback(
    (direction: 'next' | 'previous') => {
      const newNextCardIndex =
        selectedIndex < lineItemRefs.current.length - 1
          ? selectedIndex + 1
          : lineItemRefs.current.length - 1;
      const newPreviousCardIndex = selectedIndex > 0 ? selectedIndex - 1 : 0;

      const scrollToIndex = (newCardIndex: number) => {
        const cards = lineItemRefs.current;
        const targetCard = cards[newCardIndex];
        if (scrollableContainerRef.current) {
          scrollableContainerRef.current.scrollTo({
            left: targetCard.current.offsetLeft - 6,
            top: 0,
            behavior: isTablet ? 'auto' : 'smooth',
          });
        }
        setSelectedIndex(newCardIndex);
      };

      switch (true) {
        case direction === 'next':
          if (!getIsCarouselFullyScrolledToRight()) {
            scrollToIndex(newNextCardIndex);
          }
          break;
        case direction === 'previous':
          if (!getIsCarouselFullyScrolledToLeft()) {
            scrollToIndex(newPreviousCardIndex);
          }
          break;
      }
    },
    [
      getIsCarouselFullyScrolledToLeft,
      getIsCarouselFullyScrolledToRight,
      isTablet,
      lineItemRefs,
      scrollableContainerRef,
      selectedIndex,
    ],
  );

  const swipeableHandlers = useSwipeable({
    onSwipedLeft: () => handleScrollTo('next'),
    onSwipedRight: () => handleScrollTo('previous'),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  // Initialize lineItemRefs
  if (arrayLength && lineItemRefs.current.length !== arrayLength) {
    lineItemRefs.current = Array(arrayLength)
      .fill(null)
      .map((_, i) => lineItemRefs.current[i] || createRef());
  }

  // The scrollableContainerRef is initially null, so this triggers afterward
  useEffect(() => {
    setHideNextButton(getIsCarouselFullyScrolledToRight());
  }, [getIsCarouselFullyScrolledToRight, scrollableContainerRef]);

  // Show or hide buttons when container is scrolled
  useEffect(() => {
    let scrollableContainer: HTMLDivElement | null = null;

    if (scrollableContainerRef.current) {
      scrollableContainer = scrollableContainerRef.current;
      scrollableContainerRef.current.addEventListener('scroll', handleButtonToggleOnScroll);
      window.addEventListener('resize', handleButtonToggleOnScroll);
    }

    return () => {
      if (scrollableContainer) {
        scrollableContainer.removeEventListener('scroll', handleButtonToggleOnScroll);
        window.removeEventListener('resize', handleButtonToggleOnScroll);
      }
    };
  }, [handleButtonToggleOnScroll, scrollableContainerRef]);

  return {
    hideNextButton,
    hidePreviousButton,
    handleScrollTo,
    swipeableHandlers: swipeableHandlers,
  };
};
